import React, { useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tag from '@amzn/meridian/tag';
import Alert from '@amzn/meridian/alert';
import Heading from '@amzn/meridian/heading';
import useGetProgramStores from 'src/store/hooks/useGetProgramStoresResult';
import { StoreSelectorProps } from './types';

const StoreSelector: React.FC<StoreSelectorProps> = ({ onConfirm, onCancel }) => {
  const { stores: storeList, isLoading } = useGetProgramStores();
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [validationError, setValidationError] = useState('');

  const availableStores = useMemo(() => storeList, [storeList]);

  const filteredStores = useMemo(() => {
    if (!searchQuery) return availableStores;
    const q = searchQuery.toLowerCase();
    return availableStores.filter((s) => s.store.toLowerCase().includes(q) || s.label.toLowerCase().includes(q));
  }, [availableStores, searchQuery]);

  const handleStoreChange = (value: string | string[]) => {
    // Meridian multi-select passes the full array of selected values
    const values = Array.isArray(value) ? value : [value];
    setSelectedStoreIds(values);
    setValidationError('');
  };

  const handleRemoveStore = (storeId: string) => {
    setSelectedStoreIds((prev) => prev.filter((id) => id !== storeId));
  };

  const handleConfirm = () => {
    if (selectedStoreIds.length === 0) {
      setValidationError('Select at least one target store');
      return;
    }
    onConfirm(selectedStoreIds);
  };

  return (
    <Column spacing="400" spacingInset="400" maxWidth={500}>
      <Heading level={4}>Select Target Stores</Heading>

      <Select
        label="Search stores"
        value={selectedStoreIds}
        onChange={handleStoreChange}
        searchQuery={searchQuery}
        onSearch={setSearchQuery}
        placeholder={isLoading ? 'Loading stores...' : 'Type to search stores...'}
        width="100%"
        disabled={isLoading}
      >
        {isLoading && (
          <Column alignmentVertical="center" spacing="300" spacingInset="600">
            <Text alignment="center">Loading stores...</Text>
          </Column>
        )}
        {!isLoading && filteredStores.map((s) => <SelectOption key={s.store} value={s.store} label={s.label} />)}
        {!isLoading && filteredStores.length === 0 && (
          <Column alignmentVertical="center" spacing="300" spacingInset="600">
            <Text alignment="center">No matching stores</Text>
          </Column>
        )}
      </Select>

      {selectedStoreIds.length > 0 && (
        <Column spacing="200">
          <Text type="b200">Selected stores ({selectedStoreIds.length}):</Text>
          <Row spacing="200" wrap="down">
            {selectedStoreIds.map((id) => (
              <Tag key={id} onClose={() => handleRemoveStore(id)} type="theme">
                {id}
              </Tag>
            ))}
          </Row>
        </Column>
      )}

      {validationError && <Alert type="error">{validationError}</Alert>}

      <Row spacing="300">
        <Button type="primary" onClick={handleConfirm} disabled={isLoading}>
          Confirm
        </Button>
        <Button type="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </Row>
    </Column>
  );
};

export default StoreSelector;
